'use client';

/**
 * TCO Workflow Template Edit Page
 *
 * Page for editing existing workflow templates for care coordination.
 * Loads template data and allows updates to all configuration settings.
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Save,
  Loader2,
  Bot,
  Zap,
  MessageSquare,
  Users,
  Building2,
  DollarSign,
  MapPin,
  Search,
  Network,
  ClipboardList,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Workflow,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TypedStepCard } from '@/components/workflows/TypedStepCard';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/services/apiClient';
import { tcoWorkflowsAPI, WorkflowTemplateCreate, TriggerType, WorkflowTemplate } from '@/services/api/tcoServiceReferrals';
import { approvalsAPI } from '@/services/api/approvals';
import { StepTypePicker } from '@/components/workflows/StepTypePicker';
import { StepConfigurationPanel } from '@/components/workflows/StepConfigurationPanel';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { firstNotifyStepProblem, notifyStepIssues, workflowSaveErrorMessage } from '@/components/workflows/notify/notifySteps';
import { WorkflowFlowView } from '@/components/workflows/WorkflowFlowView';
import { useComplianceMode } from '@/hooks/useComplianceMode';
import { ShieldCheck } from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

// SDOH Domain options
type SDOHDomainCode = 'HOUSING' | 'FOOD_NUTRITION' | 'TRANSPORTATION' | 'ECONOMIC_STABILITY' | 'EDUCATION_EMPLOYMENT' | 'SOCIAL_SUPPORT' | 'HEALTH_BEHAVIORS' | 'SAFETY';

const SDOH_DOMAINS: { value: SDOHDomainCode; label: string }[] = [
  { value: 'HOUSING', label: 'Housing Instability' },
  { value: 'FOOD_NUTRITION', label: 'Food & Nutrition' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
  { value: 'ECONOMIC_STABILITY', label: 'Economic Stability' },
  { value: 'EDUCATION_EMPLOYMENT', label: 'Education & Employment' },
  { value: 'SOCIAL_SUPPORT', label: 'Social Support' },
  { value: 'HEALTH_BEHAVIORS', label: 'Health Behaviors' },
  { value: 'SAFETY', label: 'Safety' },
];

// Trigger type options - must match backend TriggerType enum
const TRIGGER_TYPES: { value: TriggerType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'sdoh_threshold',
    label: 'SDOH Threshold',
    description: 'Trigger when SDOH screening identifies high-risk domains',
    icon: <Users className="w-5 h-5" />,
  },
  {
    value: 'care_gap_detected',
    label: 'Care Gap Detected',
    description: 'Trigger when a care gap is identified for the member',
    icon: <Zap className="w-5 h-5" />,
  },
  {
    value: 'enrollment_milestone',
    label: 'Enrollment Milestone',
    description: 'Trigger on enrollment milestone events',
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    value: 'ai_recommended',
    label: 'AI Recommended',
    description: 'Trigger based on AI-driven recommendations',
    icon: <Bot className="w-5 h-5" />,
  },
  {
    value: 'manual',
    label: 'Manual',
    description: 'Manually triggered by navigator',
    icon: <ClipboardList className="w-5 h-5" />,
  },
];

// Channel options
const CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'portal', label: 'Member Portal' },
];

// Provider matching mode options
const PROVIDER_MATCHING_MODES = [
  { value: 'auto', label: 'Auto-Match', description: 'AI automatically finds and ranks best providers', icon: <Bot className="w-5 h-5" /> },
  { value: 'manual', label: 'Manual Selection', description: 'Navigator manually selects provider', icon: <Search className="w-5 h-5" /> },
  { value: 'network_only', label: 'Network Only', description: 'Match only from contracted network providers', icon: <Network className="w-5 h-5" /> },
  { value: 'cbo_only', label: 'CBO Only', description: 'Match only from community-based organizations', icon: <Building2 className="w-5 h-5" /> },
];

// Urgency level options
const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine', description: '14+ days to service' },
  { value: 'urgent', label: 'Urgent', description: '24-72 hours to service' },
  { value: 'emergent', label: 'Emergent', description: 'Same-day service needed' },
];

interface EditWorkflowTemplatePageProps {
  templateId: number;
  onBack: () => void;
}

export default function EditWorkflowTemplatePage({ templateId, onBack }: EditWorkflowTemplatePageProps) {
  const tabbed = useTabbedChrome();
  const { toast } = useToast();
  const { isRegulated } = useComplianceMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { t } = useAppTranslation();
  // TRUS-686: a member workflow may only "Post to channel" to private channels.
  const [memberBound, setMemberBound] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Approval gate (regulated mode only)
  const [requireApproval, setRequireApproval] = useState(false);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [approvalRequestStatus, setApprovalRequestStatus] = useState<string | null>(null);
  const [approvalSubmittedAt, setApprovalSubmittedAt] = useState<string | null>(null);

  // Basic Information
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Trigger Configuration
  const [triggerType, setTriggerType] = useState<TriggerType>('sdoh_threshold');
  const [autoTriggerEnabled, setAutoTriggerEnabled] = useState(true);
  const [selectedSdohDomains, setSelectedSdohDomains] = useState<SDOHDomainCode[]>([]);
  const [minRiskScore, setMinRiskScore] = useState(70);

  // Outreach Settings
  const [defaultChannel, setDefaultChannel] = useState('sms');
  const [maxOutreachAttempts, setMaxOutreachAttempts] = useState(3);
  const [responseTimeoutHours, setResponseTimeoutHours] = useState(48);
  const [useMlTiming, setUseMlTiming] = useState(false);

  // Referral Settings
  const [autoCreateReferral, setAutoCreateReferral] = useState(true);
  const [referralUrgencyDefault, setReferralUrgencyDefault] = useState('routine');

  // Provider Matching Settings
  const [providerMatchingMode, setProviderMatchingMode] = useState('auto');
  const [maxProvidersToMatch, setMaxProvidersToMatch] = useState(5);
  const [preferNetworkProviders, setPreferNetworkProviders] = useState(true);
  const [cboOnlyForSdoh, setCboOnlyForSdoh] = useState(true);
  const [maxDistanceMiles, setMaxDistanceMiles] = useState(25);
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [acceptanceTimeoutHours, setAcceptanceTimeoutHours] = useState(48);

  // Pricing & Billing Settings
  const [enablePricingAgent, setEnablePricingAgent] = useState(true);
  const [pricingStrategy, setPricingStrategy] = useState('balanced');
  const [maxNegotiationRounds, setMaxNegotiationRounds] = useState(5);
  const [autoBilling, setAutoBilling] = useState(true);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('invoice');

  // Workflow Steps (visual builder)
  interface WorkflowStepDef {
    name: string;
    step_type: string;
    order: number;
    config: Record<string, any>;
    form_template_id?: number | null;
    completion_requires_form?: boolean;
    documentation_required?: boolean;
    document_types?: string[];
    incentive_milestone_code?: string;
    is_critical?: boolean;
  }
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStepDef[]>([]);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [addingStep, setAddingStep] = useState(false);
  const [stepsViewMode, setStepsViewMode] = useState<'list' | 'flow'>('list');
  const [newStepType, setNewStepType] = useState('');
  const [newStepName, setNewStepName] = useState('');
  const [newStepConfig, setNewStepConfig] = useState<Record<string, any>>({});

  const addStep = () => {
    if (!newStepType || !newStepName.trim()) return;
    const step: WorkflowStepDef = {
      name: newStepName,
      step_type: newStepType,
      order: workflowSteps.length + 1,
      config: newStepConfig,
      form_template_id: newStepConfig.form_template_id || null,
      completion_requires_form: !!newStepConfig.form_template_id,
    };
    setWorkflowSteps([...workflowSteps, step]);
    setAddingStep(false);
    setNewStepType('');
    setNewStepName('');
    setNewStepConfig({});
  };

  const updateStep = (index: number) => {
    const updated = [...workflowSteps];
    updated[index] = {
      ...updated[index],
      name: newStepName || updated[index].name,
      step_type: newStepType || updated[index].step_type,
      config: newStepConfig,
      form_template_id: newStepConfig.form_template_id || null,
      completion_requires_form: !!newStepConfig.form_template_id,
    };
    setWorkflowSteps(updated);
    setEditingStepIndex(null);
    setNewStepType('');
    setNewStepName('');
    setNewStepConfig({});
  };

  const removeStep = (index: number) => {
    const updated = workflowSteps.filter((_, i) => i !== index);
    updated.forEach((s, i) => (s.order = i + 1));
    setWorkflowSteps(updated);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= workflowSteps.length) return;
    const updated = [...workflowSteps];
    [updated[index], updated[newIdx]] = [updated[newIdx], updated[index]];
    updated.forEach((s, i) => (s.order = i + 1));
    setWorkflowSteps(updated);
  };

  const startEditStep = (index: number) => {
    const step = workflowSteps[index];
    setEditingStepIndex(index);
    setNewStepName(step.name);
    setNewStepType(step.step_type);
    setNewStepConfig(step.config || {});
  };

  // Service Types (for referral creation)
  const [serviceTypes, setServiceTypes] = useState<{ code: string; name: string }[]>([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(true);

  // Load template data
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const template = await tcoWorkflowsAPI.getTemplate(templateId);

        // Populate form fields from template data
        setTemplateName(template.template_name || '');
        setDescription(template.description || '');
        setIsActive(template.is_active ?? true);
        setMemberBound((template as any).target_entity_type !== 'contact');
        setTriggerType((template.trigger_type as TriggerType) || 'sdoh_threshold');
        setAutoTriggerEnabled(template.auto_trigger_enabled ?? true);
        setSelectedSdohDomains((template.trigger_sdoh_domains as SDOHDomainCode[]) || []);
        setMinRiskScore(template.trigger_min_risk_score ?? 70);
        setDefaultChannel(template.default_channel || 'sms');
        setMaxOutreachAttempts(template.max_outreach_attempts ?? 3);
        setResponseTimeoutHours(template.response_timeout_hours ?? 48);
        setUseMlTiming(template.use_ml_timing ?? false);
        setAutoCreateReferral(template.auto_create_referral ?? true);
        setReferralUrgencyDefault(template.referral_urgency_default || 'routine');
        setSelectedServiceTypes(template.referral_service_types || []);
        setMaxProvidersToMatch(template.max_providers_to_match ?? 5);
        setPreferNetworkProviders(template.prefer_network_providers ?? true);
        setCboOnlyForSdoh(template.cbo_only_for_sdoh ?? true);

        // Load trigger_config fields
        const config = template.trigger_config || {};
        setProviderMatchingMode(config.provider_matching_mode || 'auto');
        setMaxDistanceMiles(config.max_distance_miles ?? 25);
        setRequireAcceptance(config.require_acceptance ?? true);
        setAcceptanceTimeoutHours(config.acceptance_timeout_hours ?? 48);
        setEnablePricingAgent(config.enable_pricing_agent ?? true);
        setPricingStrategy(config.pricing_strategy || 'balanced');
        setMaxNegotiationRounds(config.max_negotiation_rounds ?? 5);
        setAutoBilling(config.auto_billing ?? true);
        setDefaultPaymentMethod(config.default_payment_method || 'invoice');

        // Load workflow steps
        if (template.workflow_steps && Array.isArray(template.workflow_steps)) {
          setWorkflowSteps(template.workflow_steps.map((s: any, i: number) => ({
            name: s.name || `Step ${i + 1}`,
            step_type: s.step_type || 'task',
            order: s.order || i + 1,
            config: s.config || {},
            form_template_id: s.form_template_id || s.config?.form_template_id || null,
            completion_requires_form: s.completion_requires_form || false,
            documentation_required: s.documentation_required || false,
            document_types: s.document_types || [],
            incentive_milestone_code: s.incentive_milestone_code || undefined,
            is_critical: s.is_critical || false,
          })));
        }

      } catch (err: any) {
        console.error('Failed to load template:', err);
        setLoadError(err.response?.data?.detail || 'Failed to load template');
        toast({
          title: 'Error',
          description: 'Failed to load workflow template',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Load service types on mount
  useEffect(() => {
    const loadServiceTypes = async () => {
      try {
        const response = await apiClient.get('/api/service-referrals/taxonomy/types');
        setServiceTypes(response.data || []);
      } catch (err) {
        console.error('Failed to load service types:', err);
      } finally {
        setLoadingServiceTypes(false);
      }
    };
    loadServiceTypes();
  }, []);

  const toggleSdohDomain = (domain: SDOHDomainCode) => {
    setSelectedSdohDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  const toggleServiceType = (code: string) => {
    setSelectedServiceTypes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = async () => {
    console.log('[TCO Template Edit] handleSave called');

    if (!templateName.trim()) {
      console.log('[TCO Template Edit] Validation failed: template name empty');
      toast({
        title: 'Validation Error',
        description: 'Template name is required',
        variant: 'destructive',
      });
      return;
    }

    // TRUS-686: the server refuses a notify step it cannot run (an unknown
    // template variable, no recipients, no channel), so stop here first.
    const notifyProblem = firstNotifyStepProblem(workflowSteps, t);
    if (notifyProblem) {
      toast({
        title: t('workflows.notify.errors.fixSteps', 'Fix the workflow steps'),
        description: notifyProblem,
        variant: 'destructive',
      });
      return;
    }

    console.log('[TCO Template Edit] Starting save, template name:', templateName);
    setSaving(true);
    try {
      const templateData: Partial<WorkflowTemplateCreate> = {
        template_name: templateName,
        template_type: 'care_coordination',
        description,
        trigger_type: triggerType,
        auto_trigger_enabled: autoTriggerEnabled,
        trigger_sdoh_domains: selectedSdohDomains,
        trigger_min_risk_score: minRiskScore,
        default_channel: defaultChannel,
        max_outreach_attempts: maxOutreachAttempts,
        response_timeout_hours: responseTimeoutHours,
        use_ml_timing: useMlTiming,
        auto_create_referral: autoCreateReferral,
        referral_urgency_default: referralUrgencyDefault,
        referral_service_types: selectedServiceTypes,
        max_providers_to_match: maxProvidersToMatch,
        prefer_network_providers: preferNetworkProviders,
        cbo_only_for_sdoh: cboOnlyForSdoh,
        is_active: isActive,
        trigger_config: {
          provider_matching_mode: providerMatchingMode,
          max_distance_miles: maxDistanceMiles,
          require_acceptance: requireAcceptance,
          acceptance_timeout_hours: acceptanceTimeoutHours,
          enable_pricing_agent: enablePricingAgent,
          pricing_strategy: pricingStrategy,
          max_negotiation_rounds: maxNegotiationRounds,
          auto_billing: autoBilling,
          default_payment_method: defaultPaymentMethod,
        },
        workflow_steps: workflowSteps.map((s) => ({
          name: s.name,
          step_type: s.step_type,
          order: s.order,
          config: s.config || {},
          form_template_id: s.form_template_id || undefined,
          completion_requires_form: s.completion_requires_form || false,
          documentation_required: s.documentation_required || false,
          document_types: s.document_types || undefined,
          incentive_milestone_code: s.incentive_milestone_code || undefined,
          is_critical: s.is_critical || false,
        })),
      };

      console.log('[TCO Template Edit] Sending API request:', templateData);
      const result = await tcoWorkflowsAPI.updateTemplate(templateId, templateData);
      console.log('[TCO Template Edit] API response:', result);

      toast({
        title: 'Template Updated',
        description: `Workflow template "${templateName}" has been updated successfully.`,
      });

      onBack();
    } catch (err: any) {
      console.error('[TCO Template Edit] Failed to update workflow template:', err);
      toast({
        title: 'Error',
        description: workflowSaveErrorMessage(err, t, 'Failed to update workflow template'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setSubmittingApproval(true);
    try {
      await approvalsAPI.submit({
        subject_kind: 'workflow_template',
        subject_id: templateId,
        subject_label: templateName,
      });
      setApprovalRequestStatus('pending');
      setApprovalSubmittedAt(new Date().toISOString());
      toast({
        title: 'Submitted for review',
        description: `"${templateName}" was submitted for approval.`,
      });
    } catch (err: any) {
      console.error('Approval submit failed:', err);
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Failed to submit for review',
        variant: 'destructive',
      });
    } finally {
      setSubmittingApproval(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-5xl">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          <span className="ml-3 text-gray-500">Loading template...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-5xl">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-red-500">{loadError}</p>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          {!tabbed && (
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Edit Workflow Template
          </h1>
          )}
          <p className="text-gray-500 dark:text-gray-400">
            Modify care coordination workflow settings
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid ${isRegulated ? 'grid-cols-6' : 'grid-cols-5'} w-full`}>
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Basic
          </TabsTrigger>
          <TabsTrigger value="steps" className="flex items-center gap-2">
            <Workflow className="w-4 h-4" />
            Steps
            {workflowSteps.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{workflowSteps.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="trigger" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Triggers
          </TabsTrigger>
          <TabsTrigger value="provider" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Pricing
          </TabsTrigger>
          {isRegulated && (
            <TabsTrigger value="approval" className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Approval
            </TabsTrigger>
          )}
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Name and describe your workflow template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="templateName">Template Name *</Label>
                <Input
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., SDOH Food Insecurity Outreach"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this workflow does and when it should be triggered..."
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-sm text-gray-500">Enable this template for use</p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>
            </CardContent>
          </Card>

          {/* Referral Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Referral Settings</CardTitle>
              <CardDescription>Configure automatic referral creation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Create Service Referral</Label>
                  <p className="text-sm text-gray-500">
                    Automatically create a service referral when workflow identifies need
                  </p>
                </div>
                <Switch checked={autoCreateReferral} onCheckedChange={setAutoCreateReferral} />
              </div>

              {autoCreateReferral && (
                <>
                  <div className="space-y-2">
                    <Label>Default Urgency Level</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {URGENCY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setReferralUrgencyDefault(option.value)}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            referralUrgencyDefault === option.value
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                              : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                          }`}
                        >
                          <span className="font-medium">{option.label}</span>
                          <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Service Types for Referral</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      Select which service types this workflow can create referrals for
                    </p>
                    {loadingServiceTypes ? (
                      <div className="flex items-center gap-2 p-3 border rounded-md">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading service types...</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border rounded-md">
                        {serviceTypes.slice(0, 20).map((st) => (
                          <Badge
                            key={st.code}
                            variant={selectedServiceTypes.includes(st.code) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleServiceType(st.code)}
                          >
                            {st.name}
                          </Badge>
                        ))}
                        {serviceTypes.length > 20 && (
                          <span className="text-xs text-gray-500 self-center">
                            +{serviceTypes.length - 20} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Steps Tab */}
        <TabsContent value="steps" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Workflow Steps</CardTitle>
                  <CardDescription>
                    Define each step in the workflow. Add forms, outreach, assessments, decisions, and more.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex border rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setStepsViewMode('list')}
                      className={`px-3 py-1.5 text-xs font-medium ${stepsViewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      List
                    </button>
                    <button
                      type="button"
                      onClick={() => setStepsViewMode('flow')}
                      className={`px-3 py-1.5 text-xs font-medium ${stepsViewMode === 'flow' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Flow
                    </button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setStepsViewMode('list');
                      setAddingStep(true);
                      setEditingStepIndex(null);
                      setNewStepType('');
                      setNewStepName('');
                      setNewStepConfig({});
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Flow View */}
              {stepsViewMode === 'flow' && workflowSteps.length > 0 && (
                <WorkflowFlowView
                  steps={workflowSteps}
                  onEditStep={(idx) => { setStepsViewMode('list'); startEditStep(idx); }}
                  onRemoveStep={removeStep}
                  onAddAfter={(idx) => {
                    setStepsViewMode('list');
                    setAddingStep(true);
                  }}
                />
              )}

              {stepsViewMode === 'list' && workflowSteps.length === 0 && !addingStep && (
                <div className="text-center py-12 text-gray-500">
                  <Workflow className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No steps configured</p>
                  <p className="text-sm mt-1">Click "Add Step" to build your workflow.</p>
                </div>
              )}

              {stepsViewMode === 'list' && workflowSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-4 border rounded-lg ${
                    editingStepIndex === index ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <button type="button" onClick={() => moveStep(index, 'up')} className="p-0.5 text-gray-400 hover:text-gray-600" disabled={index === 0}>
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold text-gray-500 w-6 text-center">{step.order}</span>
                    <button type="button" onClick={() => moveStep(index, 'down')} className="p-0.5 text-gray-400 hover:text-gray-600" disabled={index === workflowSteps.length - 1}>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  {editingStepIndex === index ? (
                    <div className="flex-1 space-y-3">
                      <Input value={newStepName} onChange={(e) => setNewStepName(e.target.value)} placeholder="Step name" className="font-medium" />
                      <StepTypePicker selectedType={newStepType} onSelect={(type) => setNewStepType(type)} variant="dropdown" />
                      {newStepType && (
                        <div className="border rounded-lg p-3 bg-white">
                          <StepConfigurationPanel stepTypeValue={newStepType} baseType={newStepType.split('_')[0] as any} config={newStepConfig} onChange={setNewStepConfig} memberBound={memberBound} />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => updateStep(index)} disabled={notifyStepIssues(newStepType, newStepConfig).length > 0}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingStepIndex(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      {/* Typed step card: renders type-specific detail
                          (channel + priority for outreach, service category
                          for connect_provider, HEDIS measure for
                          care_gap_check, etc.) when step_type is one of the
                          typed catalog values. Falls back to the legacy
                          name + step_type badge for older step shapes. */}
                      <TypedStepCard step={step as any} compact />
                      <div className="mt-1 flex items-center gap-2">
                        {step.form_template_id && <Badge variant="secondary" className="text-xs">Form attached</Badge>}
                        {step.is_critical && <Badge variant="destructive" className="text-xs">Critical</Badge>}
                      </div>
                      {step.config?.message_body && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{step.config.message_body}</p>
                      )}
                    </div>
                  )}

                  {editingStepIndex !== index && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEditStep(index)}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeStep(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {stepsViewMode === 'list' && addingStep && (
                <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50 space-y-3">
                  <div className="text-sm font-medium text-blue-700">New Step #{workflowSteps.length + 1}</div>
                  <Input value={newStepName} onChange={(e) => setNewStepName(e.target.value)} placeholder="Step name (e.g., Send Consent Form, SDOH Assessment)" className="font-medium" />
                  <StepTypePicker selectedType={newStepType} onSelect={(type) => setNewStepType(type)} variant="dropdown" />
                  {newStepType && (
                    <div className="border rounded-lg p-3 bg-white">
                      <StepConfigurationPanel stepTypeValue={newStepType} baseType={newStepType.split('_')[0] as any} config={newStepConfig} onChange={setNewStepConfig} memberBound={memberBound} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addStep} disabled={!newStepType || !newStepName.trim() || notifyStepIssues(newStepType, newStepConfig).length > 0}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Step
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingStep(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trigger Configuration Tab */}
        <TabsContent value="trigger" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Trigger Configuration</CardTitle>
              <CardDescription>Define when this workflow should be triggered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Trigger Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  {TRIGGER_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setTriggerType(type.value)}
                      className={`p-4 rounded-lg border text-left transition-colors ${
                        triggerType === type.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={triggerType === type.value ? 'text-indigo-600' : 'text-gray-400'}>
                          {type.icon}
                        </span>
                        <span className="font-medium">{type.label}</span>
                      </div>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Trigger</Label>
                  <p className="text-sm text-gray-500">Automatically start workflow when conditions are met</p>
                </div>
                <Switch checked={autoTriggerEnabled} onCheckedChange={setAutoTriggerEnabled} />
              </div>

              {triggerType === 'sdoh_threshold' && (
                <>
                  <div className="space-y-2">
                    <Label>SDOH Domains to Monitor</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      Select which SDOH domains should trigger this workflow
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {SDOH_DOMAINS.map((domain) => (
                        <Badge
                          key={domain.value}
                          variant={selectedSdohDomains.includes(domain.value) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleSdohDomain(domain.value)}
                        >
                          {domain.label}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minRiskScore">Minimum Risk Score</Label>
                    <p className="text-sm text-gray-500">
                      Trigger when SDOH risk score is at or above this threshold (0-100)
                    </p>
                    <Input
                      id="minRiskScore"
                      type="number"
                      min={0}
                      max={100}
                      value={minRiskScore}
                      onChange={(e) => setMinRiskScore(parseInt(e.target.value) || 0)}
                      className="w-32"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Provider Matching Tab */}
        <TabsContent value="provider" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Provider Matching Mode</CardTitle>
              <CardDescription>Configure how providers are matched to referrals</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {PROVIDER_MATCHING_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setProviderMatchingMode(mode.value)}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      providerMatchingMode === mode.value
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={providerMatchingMode === mode.value ? 'text-indigo-600' : 'text-gray-400'}>
                        {mode.icon}
                      </span>
                      <span className="font-medium">{mode.label}</span>
                    </div>
                    <p className="text-sm text-gray-500">{mode.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Matching Parameters</CardTitle>
              <CardDescription>Fine-tune the provider matching algorithm</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxProviders">Max Providers to Match</Label>
                  <Input
                    id="maxProviders"
                    type="number"
                    min={1}
                    max={20}
                    value={maxProvidersToMatch}
                    onChange={(e) => setMaxProvidersToMatch(parseInt(e.target.value) || 5)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxDistance">Max Distance (miles)</Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <Input
                      id="maxDistance"
                      type="number"
                      min={1}
                      max={100}
                      value={maxDistanceMiles}
                      onChange={(e) => setMaxDistanceMiles(parseInt(e.target.value) || 25)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Prefer Network Providers</Label>
                  <p className="text-sm text-gray-500">
                    Prioritize contracted network providers in matching
                  </p>
                </div>
                <Switch checked={preferNetworkProviders} onCheckedChange={setPreferNetworkProviders} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>CBO Only for SDOH Services</Label>
                  <p className="text-sm text-gray-500">
                    Route SDOH-related services only to CBOs (not healthcare providers)
                  </p>
                </div>
                <Switch checked={cboOnlyForSdoh} onCheckedChange={setCboOnlyForSdoh} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Provider Acceptance</CardTitle>
              <CardDescription>Configure provider acceptance workflow</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Require Provider Acceptance</Label>
                  <p className="text-sm text-gray-500">
                    Provider must accept referral before service begins
                  </p>
                </div>
                <Switch checked={requireAcceptance} onCheckedChange={setRequireAcceptance} />
              </div>

              {requireAcceptance && (
                <div className="space-y-2">
                  <Label htmlFor="acceptanceTimeout">Acceptance Timeout (hours)</Label>
                  <p className="text-sm text-gray-500">
                    Time provider has to accept before escalation
                  </p>
                  <Input
                    id="acceptanceTimeout"
                    type="number"
                    min={1}
                    max={168}
                    value={acceptanceTimeoutHours}
                    onChange={(e) => setAcceptanceTimeoutHours(parseInt(e.target.value) || 48)}
                    className="w-32"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing & Billing Tab */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Pricing Agent</CardTitle>
              <CardDescription>Configure automated pricing negotiation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Pricing Agent</Label>
                  <p className="text-sm text-gray-500">
                    Use AI to negotiate pricing with providers automatically
                  </p>
                </div>
                <Switch checked={enablePricingAgent} onCheckedChange={setEnablePricingAgent} />
              </div>

              {enablePricingAgent && (
                <>
                  <div className="space-y-2">
                    <Label>Negotiation Strategy</Label>
                    <Select value={pricingStrategy} onValueChange={setPricingStrategy}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aggressive">Aggressive - Prioritize lowest cost</SelectItem>
                        <SelectItem value="balanced">Balanced - Balance cost and speed</SelectItem>
                        <SelectItem value="accommodating">Accommodating - Prioritize acceptance rate</SelectItem>
                        <SelectItem value="urgent">Urgent - Fast acceptance, flexible pricing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxRounds">Max Negotiation Rounds</Label>
                    <Input
                      id="maxRounds"
                      type="number"
                      min={1}
                      max={10}
                      value={maxNegotiationRounds}
                      onChange={(e) => setMaxNegotiationRounds(parseInt(e.target.value) || 5)}
                      className="w-32"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing Configuration</CardTitle>
              <CardDescription>Configure automatic billing and payment processing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Generate Billing</Label>
                  <p className="text-sm text-gray-500">
                    Automatically create billing records when service is completed
                  </p>
                </div>
                <Switch checked={autoBilling} onCheckedChange={setAutoBilling} />
              </div>

              {autoBilling && (
                <div className="space-y-2">
                  <Label>Default Payment Method</Label>
                  <Select value={defaultPaymentMethod} onValueChange={setDefaultPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice Billing</SelectItem>
                      <SelectItem value="ach">ACH Payment</SelectItem>
                      <SelectItem value="prepaid_card">Prepaid Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isRegulated && (
          <TabsContent value="approval" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-indigo-600" />
                  Approval
                </CardTitle>
                <CardDescription>
                  Require this workflow to be approved before it can run.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Require approval before this workflow can run</Label>
                    <p className="text-sm text-gray-500">
                      When enabled, this workflow must complete the approval process before it triggers.
                    </p>
                  </div>
                  <Switch checked={requireApproval} onCheckedChange={setRequireApproval} />
                </div>
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  {approvalRequestStatus ? (
                    <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                      <p>
                        <span className="font-medium">Approval status:</span>{' '}
                        <Badge variant="secondary">{approvalRequestStatus}</Badge>
                      </p>
                      {approvalSubmittedAt && (
                        <p className="text-xs text-gray-500">
                          Submitted at {new Date(approvalSubmittedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No approval request submitted yet.</p>
                  )}
                  <Button
                    type="button"
                    onClick={handleSubmitForApproval}
                    disabled={submittingApproval || !templateName.trim()}
                    className="mt-4"
                  >
                    {submittingApproval ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 mr-2" />
                    )}
                    Submit for review
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
